import type { TogglePinnedItemPayload } from '@/api/v4/pinned-items.api';
import { queryClient, QueryKey } from '@/config/query-client';
import { useCompanyFetchMode } from '@/features/companies/overview/company-fetch-context';
import type { PinnedItemsContext } from '@/features/home/dashboard/recent-items/pinned-items/use-toggle-pinned-item.mutation';
import type { CompanyData } from '@/api/v4/organization.api';

export const useOptimisticPinCompanyUpdate = () => {
  const fetchMode = useCompanyFetchMode();

  const updateCompany = (newCompanyData: CompanyData) => {
    queryClient.setQueryData(
      [QueryKey.Company, fetchMode, newCompanyData.id],
      newCompanyData,
    );
  };

  const handleOptimisticChange = async (data: TogglePinnedItemPayload) => {
    const itemId = data.id;
    const companyQueryKey = [QueryKey.Company, fetchMode, itemId];

    await queryClient.cancelQueries({ queryKey: companyQueryKey });

    const prevCompanyData = queryClient.getQueryData<CompanyData>(
      companyQueryKey,
    ) as CompanyData;

    const newCompanyData = {
      ...prevCompanyData,
      isPinned: !prevCompanyData.isPinned,
    };
    updateCompany(newCompanyData);

    return {
      prevCompanyData,
    };
  };

  const rollbackToPreviousData = (
    prevData: PinnedItemsContext,
    itemId?: string,
  ) => {
    prevData.prevCompanyData &&
      itemId &&
      queryClient.setQueryData(
        [QueryKey.Company, fetchMode, itemId],
        prevData.prevCompanyData,
      );
  };

  return {
    handleOptimisticChange,
    rollbackToPreviousData,
  };
};
